import { useCallback } from "react";
import { useOpenChatThread } from "./useOpenChatThread";
import { useOpenEmailThread } from "./useOpenEmailThread";
import { useOpenSMSThread } from "./useOpenSMSThread";
import type { InboxThread } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";

type OpenThreadArgs<T extends InboxThread["type"]> = {
  type: T;
  message: T extends "SMS"
    ? TellescopeModelFromName<"sms_messages">
    : T extends "Email"
    ? TellescopeModelFromName<"emails">
    : T extends "Chat"
    ? TellescopeModelFromName<"chat_rooms">
    : never;
  draftId?: string;
};

export const useThreadNavigation = () => {
  const openSmsThread = useOpenSMSThread();
  const openEmailThread = useOpenEmailThread();
  const openChatThread = useOpenChatThread();

  const openThread = useCallback(
    <T extends InboxThread["type"]>(args: OpenThreadArgs<T>) => {
      switch (args.type) {
        case "SMS": {
          const message = args.message as OpenThreadArgs<"SMS">["message"];
          return openSmsThread(message, args.draftId);
        }
        case "Email": {
          const message = args.message as OpenThreadArgs<"Email">["message"];
          return openEmailThread(message, args.draftId);
        }
        case "Chat": {
          const message = args.message as OpenThreadArgs<"Chat">["message"];
          return openChatThread(message, args.draftId);
        }
      }
    },
    [openChatThread, openEmailThread, openSmsThread]
  );

  return { openSmsThread, openEmailThread, openChatThread, openThread };
};
